import { addAction, removeAction } from './titleBar.store';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import guid from '@whiz-cart/node-shared/guid';
import type { LocationTarget } from '@whiz-cart/ui-shared/url/url.service';
import equals from 'fast-deep-equal/es6/react';
import { MouseEventHandler, ReactElement, useEffect, useRef, useState, type HTMLProps } from 'react';

export type ActionConfig<
    TAction extends string | LocationTarget | MouseEventHandler<HTMLDivElement> =
        | string
        | LocationTarget
        | MouseEventHandler<HTMLDivElement>,
> = {
    index?: number;
    label?: React.ReactNode;
    id: string;
    display?: boolean;
    disabled?: boolean | React.ReactNode;
    icon?: ReactElement | IconProp;
    titleBarLinkId?: string;
    action: TAction;
    actionProps?: HTMLProps<HTMLDivElement> & HTMLProps<HTMLAnchorElement>;
    className?: string;
    isLoading?: boolean;
    userGuid?: string;
};

export function useAction<TAction extends string | LocationTarget | MouseEventHandler<HTMLDivElement>>(
    config: Omit<ActionConfig<TAction>, 'action'>,
    action: ActionConfig<TAction>['action'] = ((_e) => undefined) as TAction,
    deps: React.DependencyList = [action],
): TAction {
    const [id] = useState(config.id ?? guid());
    const configRef = useRef(config);

    useEffect(() => {
        configRef.current = config;

        if (!('display' in config) || config.display) {
            addAction({ ...config, action, id });
        } else removeAction(id);

        return () => {
            removeAction(id);
        };
    }, [equals(config, configRef.current) ? configRef.current : config, ...deps]);

    return action;
}
